import React, { useCallback, useEffect, useRef, useState } from "react";
/* MapLibre GL 6 ships named exports only — there is no default export. A
 * default import yields undefined, so the usual `maplibregl.Map(...)` pattern
 * found in most tutorials fails to build here. Import each piece by name. */
import {
  Map as MapLibreMap,
  AttributionControl,
  NavigationControl,
  ScaleControl,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import {
  buildBaseStyle, resolveProvider, OSM_ATTRIBUTION,
  isVectorBasemap, vectorStyleUrl,
} from "../config/tileProviders";
import { LAYERS, OVERLAYS, GEOM } from "../config/layers";
import { MAP_HOME, THEATRE_BOUNDS } from "../data/kumbhGeo";
import {
  buildPointCollections, buildSectorCollection, buildCongregationCollection,
  buildRouteCollections, buildCrowdCollection, buildIncidentCollection,
  buildFlowCollection,
} from "../services/geojson";

/* ──────────────────────────────────────────────────────────────────────────
 * The operational map.
 *
 * Design decisions worth knowing:
 *
 * - The map instance is created ONCE and never rebuilt. Layer toggles change
 *   `visibility` on existing layers, and data changes call setData on the
 *   source. Tearing down and recreating on every toggle is the usual cause of
 *   a map that stutters with real data volumes (§13: "do not unnecessarily
 *   rerender the entire map when one event changes").
 *
 * - Points cluster, and clustering is per-layer rather than global. A cluster
 *   that mixes hospitals with toilets tells an operator nothing.
 *
 * - Zoom-dependent rendering: labels appear only when there is room for them,
 *   so the map reads as a command surface at every zoom rather than a wall of
 *   pins (§7).
 *
 * - Attribution is non-removable (ODbL licence condition, §12).
 * ────────────────────────────────────────────────────────────────────────── */

const CLUSTER_MAX_ZOOM = 13;

export default function KumbhGeoMap({
  lang = "en",
  layerState,
  overlayState,
  crowdPoints,
  incidents,
  flowVectors,
  allowedLayerIds,
  focusTarget,
  onFeatureClick,
  onMapReady,
  simulation,        // projected geometry to overlay (§5)
  pickMode,          // when set, the next map click returns a coordinate
  onPickPoint,
  importedRecords,   // records ingested from a file (§10)
  chokepoints,       // predicted bottlenecks from the risk engine
  riskZones,         // scored zones from the risk engine
  decisions,         // engine recommendations, pinned to the map
  basemap = "osmRaster",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  /* Development-only self-report. Diagnosing "the map is blank" by asking
     someone to paste console output is slow and error-prone; putting the
     answer on the map turns it into a single glance. Never rendered in a
     production build. */
  const [diag, setDiag] = useState(null);
  /* Bumped whenever the style is replaced. A vector basemap is a WHOLE style,
     so switching to it calls setStyle(), which discards every source and
     layer we added. Nothing survives that — so every effect that installs or
     feeds a layer depends on this epoch and re-runs after each switch. */
  const [styleEpoch, setStyleEpoch] = useState(0);
  const provider = useRef(resolveProvider()).current;

  /* Keep the newest callback without making it a map-effect dependency —
   * otherwise every parent render would rewire map event handlers. */
  const clickRef = useRef(onFeatureClick);
  clickRef.current = onFeatureClick;
  const vectorRef = useRef(false);   // is a vector style currently mounted?
  const vectorIdRef = useRef(null);  // which vector style, if any
  const pickRef = useRef(null);
  pickRef.current = { pickMode, onPickPoint };

  /* ── Create the map once ───────────────────────────────────────────── */
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return undefined;

    let map;
    try {
      map = new MapLibreMap({
        container: containerRef.current,
        style: buildBaseStyle(provider),
        center: MAP_HOME.center,
        zoom: MAP_HOME.zoom,
        maxBounds: THEATRE_BOUNDS,
        attributionControl: false, // added explicitly below so it cannot be dropped
        dragRotate: false,         // a command map should keep north up
      });
    } catch (err) {
      setLoadError(err?.message || "Map failed to initialise");
      return undefined;
    }
    mapRef.current = map;

    map.addControl(
      new AttributionControl({ compact: false, customAttribution: OSM_ATTRIBUTION }),
      "bottom-right"
    );
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new ScaleControl({ maxWidth: 120, unit: "metric" }), "bottom-left");

    map.on("error", (e) => {
      /* Log everything, but blank the map for almost nothing.
       *
       * Two wrong versions preceded this one. The first only reported errors
       * carrying an HTTP status, which silently swallowed style errors. The
       * second over-corrected and set a fatal error for ANY message — so a
       * single failing tile replaced a perfectly working map with a failure
       * screen. That is what a CORS-blocked DEM source did here.
       *
       * The rule: tile and source failures degrade the map, they do not
       * break it. Only a failure to load the style itself is fatal, because
       * after that there is genuinely nothing to show. */
      const err = e?.error || e;
      // eslint-disable-next-line no-console
      console.error("[KumbhGeoMap] MapLibre error:", err?.message || err, err);

      const msg = err?.message || "";
      const isTileOrSource =
        err?.status != null ||
        e?.sourceId != null ||
        /tile|source|fetch|network|load image|AbortError/i.test(msg);

      // A style that never loads leaves nothing to render; that is fatal.
      const styleFailed = !map.isStyleLoaded() && /style/i.test(msg);

      if (styleFailed) setLoadError(msg || "Style failed to load");
      // everything else: logged above, map keeps working
    });

    /* Readiness is latched from any of three signals rather than `load` alone.
     *
     * `load` is the normal path. `idle` covers the case where we attached
     * after the map had already settled, and `map.loaded()` covers attaching
     * later still. Belt and braces: if one signal is missed the map is
     * usable rather than stuck on a loading overlay forever.
     *
     * Note for anyone debugging a map that never becomes ready: MapLibre
     * schedules style loading on requestAnimationFrame, which browsers do
     * not run in a hidden or background tab. A map in an unfocused tab can
     * therefore sit at isStyleLoaded() === false indefinitely and look
     * broken while being perfectly healthy. Check document.visibilityState
     * before concluding anything. */
    let readyLatched = false;
    const markReady = () => {
      if (readyLatched) return;
      readyLatched = true;
      setReady(true);
      map.resize();      // see ResizeObserver note below
      onMapReady?.(map);
    };

    if (map.loaded()) markReady();
    map.on("load", markReady);
    map.on("idle", markReady);
    map.on("styledata", () => { if (map.isStyleLoaded()) markReady(); });

    /* MapLibre measures its container once, at construction. Inside a flex
     * layout the container is frequently still 0×0 at that moment — the
     * parent has not been laid out yet — and the map then renders into a
     * zero-size canvas and appears blank or a sliver, with no error. It never
     * recovers on its own because nothing tells it the box changed.
     *
     * Observing the container and calling resize() fixes both that first
     * paint and every later change: collapsing the layer panel, rotating a
     * tablet, or dragging the window. */
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
    };
    // Intentionally empty: the map is created once for the component's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Install sources and layers once the style is loaded ───────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const pointCollections = buildPointCollections(lang);
    const routeCollections = buildRouteCollections(lang);

    /* Sectors — fill + outline, coloured by G/A/R status. Drawn first so
     * everything else sits above them. */
    addSource(map, "sectors", buildSectorCollection(lang));
    addLayer(map, {
      id: "sectors-fill", type: "fill", source: "sectors",
      paint: {
        /* 0.32 was chosen against a pale basemap and disappears over relief
           and imagery. Zone status is the single most important thing this
           map says, so it is allowed to be assertive. */
        "fill-color": ["get", "statusColor"],
        "fill-opacity": 0.45,
      },
    });
    /* Boundaries get a CASING: a wider dark line drawn underneath a
       narrower coloured one. A single coloured stroke is legible over a pale
       street map and disappears over satellite imagery, dense urban tiles or
       its own fill. The casing gives the boundary a constant dark edge, so
       the colour reads as status rather than fighting the background — this
       is standard cartographic practice, not decoration. */
    addLayer(map, {
      id: "sectors-casing", type: "line", source: "sectors",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": "rgba(15,23,42,0.75)",
        "line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 15, 9],
        "line-blur": 0.4,
      },
    });
    addLayer(map, {
      id: "sectors-line", type: "line", source: "sectors",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": ["get", "statusColor"],
        "line-width": ["interpolate", ["linear"], ["zoom"], 10, 2.5, 15, 5],
        "line-opacity": 1,
      },
    });
    addLayer(map, {
      id: "sectors-label", type: "symbol", source: "sectors",
      layout: {
        /* to-string is not optional: concat accepts strings only, and passing
             the numeric ratioPct throws a type error that fails this layer
             and, with it, the style. */
        "text-field": ["concat", ["get", "name"], "  ", ["to-string", ["get", "ratioPct"]], "%"],
        "text-size": 12, "text-font": ["Noto Sans Regular"],
        "text-allow-overlap": false,
      },
      paint: { "text-color": "#0F172A", "text-halo-color": "#FFFFFF", "text-halo-width": 1.6 },
    });

    /* Congregation areas */
    addSource(map, "congregation", buildCongregationCollection(lang));
    addLayer(map, {
      id: "congregation-fill", type: "fill", source: "congregation",
      paint: { "fill-color": "#7C3AED", "fill-opacity": 0.18 },
    });
    addLayer(map, {
      id: "congregation-line", type: "line", source: "congregation",
      paint: { "line-color": "#7C3AED", "line-width": 1.5, "line-dasharray": [2, 1] },
    });

    /* Crowd heatmap — weight-driven, fading out as we zoom in and individual
     * features become readable instead. */
    addSource(map, "crowd", buildCrowdCollection(crowdPoints));
    addLayer(map, {
      id: "crowd-heat", type: "heatmap", source: "crowd",
      paint: {
        "heatmap-weight": ["interpolate", ["linear"], ["get", "weight"], 0, 0, 1, 1],
        "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 9, 0.8, 15, 2.2],
        "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 9, 18, 15, 46],
        "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 9, 0.75, 16, 0.35],
        // Green → amber → red, matching the §2 status language exactly.
        "heatmap-color": [
          "interpolate", ["linear"], ["heatmap-density"],
          0,    "rgba(16,185,129,0)",
          0.2,  "rgba(16,185,129,0.55)",
          0.45, "rgba(245,158,11,0.65)",
          0.7,  "rgba(249,115,22,0.75)",
          1,    "rgba(239,68,68,0.88)",
        ],
      },
    });

    /* Routes */
    Object.entries(routeCollections).forEach(([layerId, collection]) => {
      addSource(map, `route-${layerId}`, collection);
      const def = LAYERS.find((l) => l.id === layerId);
      addLayer(map, {
        id: `${layerId}-line`, type: "line", source: `route-${layerId}`,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": def?.color ?? "#475569",
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 2, 15, 5],
          "line-opacity": 0.9,
          // Evacuation and diversion routes are dashed: they are contingency
          // geometry, not roads that exist as drawn.
          ...(layerId === "evacuationRoutes" || layerId === "diversionRoutes"
            ? { "line-dasharray": [2, 1.5] } : {}),
        },
      });
    });

    /* Point layers — clustered per layer */
    Object.entries(pointCollections).forEach(([layerId, collection]) => {
      const def = LAYERS.find((l) => l.id === layerId);
      const color = def?.color ?? "#475569";

      addSource(map, `pts-${layerId}`, collection, {
        cluster: true, clusterMaxZoom: CLUSTER_MAX_ZOOM, clusterRadius: 44,
      });

      addLayer(map, {
        id: `${layerId}-cluster`, type: "circle", source: `pts-${layerId}`,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": color, "circle-opacity": 0.85,
          "circle-radius": ["step", ["get", "point_count"], 14, 5, 18, 15, 24],
          "circle-stroke-width": 2, "circle-stroke-color": "#FFFFFF",
        },
      });
      addLayer(map, {
        id: `${layerId}-cluster-count`, type: "symbol", source: `pts-${layerId}`,
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-size": 11, "text-font": ["Noto Sans Regular"],
        },
        paint: { "text-color": "#FFFFFF" },
      });
      addLayer(map, {
        id: `${layerId}-point`, type: "circle", source: `pts-${layerId}`,
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": color,
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 11, 4, 16, 8],
          "circle-stroke-width": 2, "circle-stroke-color": "#FFFFFF",
        },
      });
      addLayer(map, {
        id: `${layerId}-label`, type: "symbol", source: `pts-${layerId}`,
        // Labels only past the clustering zoom, or they collide into mush.
        minzoom: CLUSTER_MAX_ZOOM,
        filter: ["!", ["has", "point_count"]],
        layout: {
          "text-field": ["get", "name"], "text-size": 11,
          "text-font": ["Noto Sans Regular"],
          "text-offset": [0, 1.2], "text-anchor": "top",
          "text-allow-overlap": false,
        },
        paint: { "text-color": "#0F172A", "text-halo-color": "#FFFFFF", "text-halo-width": 1.5 },
      });
    });

    /* Flow arrows */
    addSource(map, "flow", buildFlowCollection(flowVectors, lang));
    addLayer(map, {
      id: "flowArrows-symbol", type: "symbol", source: "flow",
      layout: {
        "text-field": "▲", "text-size": 20,
        "text-font": ["Noto Sans Regular"],
        "text-rotate": ["get", "bearing"],
        "text-rotation-alignment": "map",
        "text-allow-overlap": true,
      },
      paint: { "text-color": "#0F172A", "text-halo-color": "#FFFFFF", "text-halo-width": 1.5 },
    });

    /* Incidents — always on top, sized and coloured by severity. */
    addSource(map, "incidents", buildIncidentCollection(incidents, lang));
    addLayer(map, {
      id: "incidents-halo", type: "circle", source: "incidents",
      filter: [">=", ["get", "severityRank"], 2],
      paint: {
        "circle-color": ["get", "color"], "circle-opacity": 0.22,
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 12, 16, 30],
      },
    });
    addLayer(map, {
      id: "incidents-point", type: "circle", source: "incidents",
      paint: {
        "circle-color": ["get", "color"],
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 6, 16, 12],
        "circle-stroke-width": 2.5, "circle-stroke-color": "#FFFFFF",
      },
    });

    /* Simulation overlay (§5). Rendered in fuchsia and dashed throughout —
       the same hue as the SIMULATED trust badge — so projected geometry is
       never mistaken for observed geometry at a glance. */
    addSource(map, "sim-line", { type: "FeatureCollection", features: [] });
    addLayer(map, {
      id: "sim-line-layer", type: "line", source: "sim-line",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#C026D3", "line-width": 5,
        "line-dasharray": [1.5, 1], "line-opacity": 0.95,
      },
    });
    addSource(map, "sim-point", { type: "FeatureCollection", features: [] });
    addLayer(map, {
      id: "sim-point-halo", type: "circle", source: "sim-point",
      paint: {
        "circle-color": "#C026D3", "circle-opacity": 0.18,
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 14, 16, 42],
      },
    });
    addLayer(map, {
      id: "sim-point-layer", type: "circle", source: "sim-point",
      paint: {
        "circle-color": "#C026D3",
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 6, 16, 11],
        "circle-stroke-width": 2.5, "circle-stroke-color": "#FFFFFF",
      },
    });

    /* Imported records (§10) — distinct from both observed and simulated. */
    addSource(map, "imported", { type: "FeatureCollection", features: [] });
    addLayer(map, {
      id: "imported-line", type: "line", source: "imported",
      filter: ["==", ["geometry-type"], "LineString"],
      paint: { "line-color": "#0EA5E9", "line-width": 3, "line-opacity": 0.9 },
    });
    addLayer(map, {
      id: "imported-point", type: "circle", source: "imported",
      filter: ["==", ["geometry-type"], "Point"],
      paint: {
        "circle-color": "#0EA5E9", "circle-radius": 6,
        "circle-stroke-width": 2, "circle-stroke-color": "#FFFFFF",
      },
    });

    /* ── Risk beacons ──────────────────────────────────────────────────
       The sector polygons are a few hundred metres across; at the theatre
       zoom (both cities in frame) they are specks, so their green/amber/red
       fill communicates nothing at the zoom an operator actually starts at.
       These beacons sit at each sector centre and stay legible at every
       zoom: colour carries the status band, radius carries the risk score.
       This is what makes the map readable as a status board rather than a
       street map with some tinting on it. */
    addSource(map, "risk", { type: "FeatureCollection", features: [] });
    addLayer(map, {
      id: "risk-glow", type: "circle", source: "risk",
      paint: {
        "circle-color": ["get", "color"],
        "circle-opacity": 0.22,
        /* Interpolated on the SCORE, not on zoom. Mixing a zoom input with
           data expressions in the outputs is a composite function, which is
           easy to get subtly wrong and fails the whole style when it is. A
           pure data expression gives the same "bigger means worse" reading
           and always validates. */
        "circle-radius": ["interpolate", ["linear"], ["get", "score"], 0, 14, 100, 46],
      },
    });
    addLayer(map, {
      id: "risk-core", type: "circle", source: "risk",
      paint: {
        "circle-color": ["get", "color"],
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 9, 15, 18],
        "circle-stroke-width": 2.5,
        "circle-stroke-color": "#FFFFFF",
      },
    });
    addLayer(map, {
      id: "risk-score", type: "symbol", source: "risk",
      layout: {
        "text-field": ["to-string", ["get", "score"]],
        "text-size": ["interpolate", ["linear"], ["zoom"], 9, 10, 15, 14],
        "text-font": ["Noto Sans Regular"],
        "text-allow-overlap": true,
      },
      paint: { "text-color": "#FFFFFF", "text-halo-color": "rgba(0,0,0,0.35)", "text-halo-width": 1 },
    });
    addLayer(map, {
      id: "risk-caption", type: "symbol", source: "risk",
      minzoom: 11.5,
      layout: {
        "text-field": ["get", "caption"],
        "text-size": 11,
        "text-font": ["Noto Sans Regular"],
        "text-offset": [0, 1.9],
        "text-anchor": "top",
        "text-allow-overlap": false,
      },
      paint: { "text-color": "#0F172A", "text-halo-color": "#FFFFFF", "text-halo-width": 1.7 },
    });

    /* ── Decision pins ─────────────────────────────────────────────────
       Recommendations were previously only readable in the side panel, so
       the map could not answer "where should I look?". Each pin is placed
       at its recommendation's focus point and coloured by priority. */
    addSource(map, "decisions", { type: "FeatureCollection", features: [] });
    addLayer(map, {
      id: "decision-pin", type: "circle", source: "decisions",
      paint: {
        "circle-color": ["get", "color"],
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 7, 15, 12],
        "circle-stroke-width": 3,
        "circle-stroke-color": "#FFFFFF",
      },
    });
    addLayer(map, {
      id: "decision-label", type: "symbol", source: "decisions",
      minzoom: 11,
      layout: {
        "text-field": ["get", "short"],
        "text-size": 10,
        "text-font": ["Noto Sans Regular"],
        "text-offset": [0, -1.6],
        "text-anchor": "bottom",
        "text-allow-overlap": false,
      },
      paint: { "text-color": "#7F1D1D", "text-halo-color": "#FFFFFF", "text-halo-width": 1.7 },
    });

    /* Predicted chokepoints. Drawn above everything, with a wide soft ring,
       so a projected pinch point is visible at theatre zoom without needing
       the operator to already know where to look. */
    addSource(map, "chokepoints", { type: "FeatureCollection", features: [] });
    addLayer(map, {
      id: "chokepoint-ring", type: "circle", source: "chokepoints",
      paint: {
        "circle-color": "#EA580C", "circle-opacity": 0.2,
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 16, 15, 54],
        "circle-stroke-width": 2, "circle-stroke-color": "#EA580C",
        "circle-stroke-opacity": 0.65,
      },
    });
    addLayer(map, {
      id: "chokepoint-label", type: "symbol", source: "chokepoints",
      layout: {
        "text-field": ["concat", ["to-string", ["get", "ratio"]], "×"],
        "text-size": 12, "text-font": ["Noto Sans Regular"],
        "text-allow-overlap": true,
      },
      paint: { "text-color": "#7C2D12", "text-halo-color": "#FFFFFF", "text-halo-width": 1.8 },
    });

    /* Click + cursor handling across every interactive layer. */
    const interactive = map.getStyle().layers
      .filter((l) => /-point$|-fill$|-line$|-cluster$/.test(l.id))
      .map((l) => l.id);

    const onClick = (e) => {
      // Scenario point-picking takes precedence over feature selection.
      if (pickRef.current?.pickMode) {
        pickRef.current.onPickPoint?.([e.lngLat.lng, e.lngLat.lat]);
        return;
      }
      const hits = map.queryRenderedFeatures(e.point, { layers: interactive });
      if (!hits.length) return;
      const f = hits[0];
      if (f.properties?.point_count) {
        map.easeTo({ center: e.lngLat, zoom: Math.min(map.getZoom() + 2, 17) });
        return;
      }
      clickRef.current?.(f.properties, f.layer.id);
    };
    const onEnter = () => { map.getCanvas().style.cursor = "pointer"; };
    const onLeave = () => { map.getCanvas().style.cursor = ""; };

    map.on("click", onClick);
    interactive.forEach((id) => {
      map.on("mouseenter", id, onEnter);
      map.on("mouseleave", id, onLeave);
    });

    return () => {
      map.off("click", onClick);
      interactive.forEach((id) => {
        map.off("mouseenter", id, onEnter);
        map.off("mouseleave", id, onLeave);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, lang, styleEpoch]);

  /* ── Toggle visibility without rebuilding anything ─────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    LAYERS.forEach((def) => {
      // A layer the role may not see is hidden regardless of its toggle.
      const permitted = !allowedLayerIds || allowedLayerIds.includes(def.id);
      const on = permitted && !!layerState?.[def.id];
      const ids =
        def.geom === GEOM.POLYGON
          ? [`${def.id}-fill`, `${def.id}-casing`, `${def.id}-line`, `${def.id}-label`]
          : def.geom === GEOM.LINE
          ? [`${def.id}-line`]
          : [`${def.id}-cluster`, `${def.id}-cluster-count`, `${def.id}-point`, `${def.id}-label`];
      ids.forEach((id) => setVisible(map, id, on));
    });

    setVisible(map, "crowd-heat", !!overlayState?.crowdHeat);
    ["sectors-fill", "sectors-casing", "sectors-line", "sectors-label"].forEach((id) =>
      setVisible(map, id, !!layerState?.sectors && !!overlayState?.zoneStatus));
    setVisible(map, "flowArrows-symbol", !!overlayState?.flowArrows);
    setVisible(map, "incidents-point", !!overlayState?.incidents);
    setVisible(map, "incidents-halo", !!overlayState?.incidents);

    /* addLayer() creates everything hidden by default, so these must be
       switched on explicitly or a scenario runs and draws nothing. */
    ["sim-line-layer", "sim-point-halo", "sim-point-layer"].forEach((id) =>
      setVisible(map, id, true));
    ["imported-line", "imported-point"].forEach((id) => setVisible(map, id, true));
    ["chokepoint-ring", "chokepoint-label"].forEach((id) => setVisible(map, id, true));
    /* Risk beacons follow the zone-status overlay toggle; decision pins
       follow the incidents toggle, since both are "what needs attention". */
    ["risk-glow", "risk-core", "risk-score", "risk-caption"].forEach((id) =>
      setVisible(map, id, !!overlayState?.zoneStatus));
    ["decision-pin", "decision-label"].forEach((id) =>
      setVisible(map, id, !!overlayState?.incidents));
  }, [ready, layerState, overlayState, allowedLayerIds, styleEpoch]);

  /* ── Live data updates: setData only, never a rebuild ──────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    map.getSource("incidents")?.setData(buildIncidentCollection(incidents, lang));
  }, [ready, incidents, lang, styleEpoch]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    map.getSource("crowd")?.setData(buildCrowdCollection(crowdPoints));
  }, [ready, crowdPoints, styleEpoch]);

  /* ── Simulation overlay: setData only, never a rebuild ───────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const lines = [];
    const points = [];

    if (simulation?.ok) {
      const geom = simulation.geometry || simulation.best?.geometry;
      if (geom?.length) {
        lines.push({ type: "Feature", geometry: { type: "LineString", coordinates: geom }, properties: {} });
      }
      (simulation.diversions || []).forEach((d) =>
        lines.push({ type: "Feature", geometry: { type: "LineString", coordinates: d.geometry }, properties: {} }));
      if (simulation.origin) {
        points.push({ type: "Feature", geometry: { type: "Point", coordinates: simulation.origin }, properties: {} });
      }
      (simulation.newlyCritical || []).forEach((r) =>
        points.push({ type: "Feature", geometry: { type: "Point", coordinates: r.centre }, properties: {} }));
      (simulation.found || []).slice(0, 60).forEach((f) =>
        points.push({ type: "Feature", geometry: { type: "Point", coordinates: f.coord }, properties: {} }));
    }

    map.getSource("sim-line")?.setData({ type: "FeatureCollection", features: lines });
    map.getSource("sim-point")?.setData({ type: "FeatureCollection", features: points });
  }, [ready, simulation, styleEpoch]);

  /* ── Imported records ──────────────────────────────────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const features = (importedRecords || []).map((r) => {
      if (r.kind === "point") return { type: "Feature", geometry: { type: "Point", coordinates: r.coord }, properties: { id: r.id } };
      if (r.kind === "line") return { type: "Feature", geometry: { type: "LineString", coordinates: r.coords }, properties: { id: r.id } };
      if (r.kind === "polygon") return { type: "Feature", geometry: { type: "LineString", coordinates: [...r.polygon, r.polygon[0]] }, properties: { id: r.id } };
      return null;
    }).filter(Boolean);
    map.getSource("imported")?.setData({ type: "FeatureCollection", features });
  }, [ready, importedRecords, styleEpoch]);

  /* ── Basemap switch ────────────────────────────────────────────────
     Two mechanisms, because the two kinds of basemap are not the same kind
     of thing:

       RASTER (street / satellite) — both are layers inside OUR style, so
         switching is a visibility toggle. Instant, and every operational
         layer stays exactly where it is.

       VECTOR — a vector basemap is a COMPLETE style document owned by the
         provider. It cannot be slotted in as a layer; it replaces the style
         via setStyle(), which discards every source and layer we added. So
         once the new style is in we bump styleEpoch, and the install effects
         re-add everything on top of it.

     The alternative — keeping our layers on a second overlay map — costs an
     extra WebGL context and keeps two viewports in sync forever.
     Re-installing is cheaper, and the layer definitions are declarative. */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return undefined;

    const wantVector = isVectorBasemap(basemap);
    const swapNeeded =
      wantVector !== vectorRef.current ||
      (wantVector && basemap !== vectorIdRef.current);

    if (swapNeeded) {
      const nextStyle = wantVector ? vectorStyleUrl(basemap) : buildBaseStyle();
      vectorRef.current = wantVector;
      vectorIdRef.current = wantVector ? basemap : null;

      const onStyleReady = () => {
        if (!map.isStyleLoaded()) return;
        map.off("styledata", onStyleReady);
        setStyleEpoch((e) => e + 1);   // triggers re-installation
      };
      map.on("styledata", onStyleReady);
      map.setStyle(nextStyle);
      return () => map.off("styledata", onStyleReady);
    }

    // Raster street <-> satellite is only a visibility change.
    const sat = basemap === "s2cloudless";
    setVisible(map, "satellite", sat);
    setVisible(map, "basemap", !sat);

    /* Sector fills and labels were tuned against a pale street map; over
       dark imagery they need more contrast, so they lift with the basemap. */
    if (map.getLayer("sectors-casing")) {
      // A heavier casing over imagery, where there is more to compete with.
      map.setPaintProperty("sectors-casing", "line-color",
        sat ? "rgba(0,0,0,0.85)" : "rgba(15,23,42,0.75)");
    }
    if (map.getLayer("sectors-fill")) {
      map.setPaintProperty("sectors-fill", "fill-opacity", sat ? 0.38 : 0.45);
    }
    ["sectors-label", "risk-caption"].forEach((id) => {
      if (!map.getLayer(id)) return;
      map.setPaintProperty(id, "text-color", sat ? "#FFFFFF" : "#0F172A");
      map.setPaintProperty(id, "text-halo-color", sat ? "rgba(0,0,0,0.75)" : "#FFFFFF");
    });
    return undefined;
  }, [ready, basemap, styleEpoch]);

  /* ── Risk beacons ──────────────────────────────────────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const BAND = { red: "#DC2626", amber: "#F59E0B", green: "#10B981" };
    const features = (riskZones || []).map((z) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: z.centre },
      properties: {
        id: z.sectorId,
        score: z.score,
        color: BAND[z.band] || "#64748B",
        caption: `${z.name?.[lang] ?? z.name?.en ?? z.sectorId} · ${z.occupancyPct}%`,
      },
    }));
    map.getSource("risk")?.setData({ type: "FeatureCollection", features });
  }, [ready, riskZones, lang, styleEpoch]);

  /* ── Decision pins ─────────────────────────────────────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const PRIORITY = { critical: "#DC2626", high: "#EA580C", moderate: "#D97706", low: "#0891B2" };
    const features = (decisions || [])
      .filter((d) => Array.isArray(d.focus))
      .map((d) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: d.focus },
        properties: {
          id: d.id,
          color: PRIORITY[d.priority] || "#64748B",
          short: d.leadMinutes != null ? `T−${d.leadMinutes}m` : d.kind,
        },
      }));
    map.getSource("decisions")?.setData({ type: "FeatureCollection", features });
  }, [ready, decisions, styleEpoch]);

  /* ── Predicted chokepoints ─────────────────────────────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const features = (chokepoints || []).map((c) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: c.centre },
      properties: { id: c.sectorId, ratio: c.demandRatio },
    }));
    map.getSource("chokepoints")?.setData({ type: "FeatureCollection", features });
  }, [ready, chokepoints, styleEpoch]);

  /* ── Cursor reflects pick mode ─────────────────────────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    map.getCanvas().style.cursor = pickMode ? "crosshair" : "";
  }, [ready, pickMode]);

  /* ── Focus (search result / panel selection) ───────────────────────── */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !focusTarget?.coord) return;
    map.easeTo({ center: focusTarget.coord, zoom: focusTarget.zoom ?? 15, duration: 900 });
  }, [ready, focusTarget]);

  /* Sample the map's real state on every idle, so the readout reflects what
     is actually loaded rather than what we intended to load. */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || process.env.NODE_ENV === "production") return undefined;

    const sample = () => {
      try {
        const style = map.getStyle();
        const vis = (id) =>
          map.getLayer(id) ? (map.getLayoutProperty(id, "visibility") ?? "visible") : "MISSING";
        const featureCount = (id) => {
          const src = map.getSource(id);
          if (!src) return "no source";
          const d = src._data;
          return d && d.features ? d.features.length : "n/a";
        };
        setDiag({
          styleLoaded: map.isStyleLoaded(),
          zoom: map.getZoom().toFixed(1),
          layers: style.layers.length,
          base: vis("basemap"),
          sat: vis("satellite"),
          sectors: `${vis("sectors-fill")}/${featureCount("sectors")}`,
          risk: `${vis("risk-core")}/${featureCount("risk")}`,
          heat: `${vis("crowd-heat")}/${featureCount("crowd")}`,
          inc: `${vis("incidents-point")}/${featureCount("incidents")}`,
        });
      } catch { /* sampling must never break the map */ }
    };

    sample();
    map.on("idle", sample);
    return () => map.off("idle", sample);
  }, [ready]);

  const resetView = useCallback(() => {
    mapRef.current?.easeTo({ ...MAP_HOME, duration: 800 });
  }, []);

  /* `absolute inset-0`, NOT `w-full h-full`.
   *
   * A percentage height only resolves against a parent with a DEFINITE
   * height. On desktop this component's parent is a stretched flex item whose
   * computed height is `auto`, so `h-full` collapses to zero, the canvas gets
   * no height, and the map renders as blank white — with no error anywhere,
   * because nothing actually failed. Filling the positioned parent absolutely
   * sidesteps percentage resolution entirely and works at every breakpoint. */
  return (
    <div className="absolute inset-0">
      {/* Sized with width/height, NOT with `absolute inset-0`.
       *
       * MapLibre adds `.maplibregl-map` to this element, and its stylesheet
       * declares `.maplibregl-map { position: relative }`. That overrides an
       * `absolute` utility on the same element, which drops the inset
       * anchoring, leaves height:auto, and — with no children of its own —
       * collapses the container to 0px. The map then has no box to render
       * into and never fires `load`, so the component sits on its loading
       * state forever with nothing in the console.
       *
       * w/h percentages resolve here because the parent above is absolutely
       * positioned with insets, giving it a definite height. */}
      <div ref={containerRef} className="w-full h-full" />

      {/* Loading state (§15) */}
      {!ready && !loadError && (
        <div className="absolute inset-0 grid place-items-center bg-slate-100">
          <div className="text-xs font-semibold text-slate-500">Loading operational map…</div>
        </div>
      )}

      {/* Graceful degradation: say what broke and keep the app usable. */}
      {loadError && (
        <div className="absolute inset-0 grid place-items-center bg-slate-100 p-6 text-center">
          <div>
            <div className="text-sm font-black text-slate-700">Base map unavailable</div>
            <div className="mt-1 text-xs text-slate-500 max-w-sm">{loadError}</div>
            <div className="mt-2 text-[11px] text-slate-400">
              Operational data is unaffected — panels continue to work.
            </div>
          </div>
        </div>
      )}

      {/* Provider fallback notice — only when configuration silently degraded. */}
      {provider.fallbackReason && (
        <div className="absolute top-2 left-2 z-10 rounded-md bg-amber-50 border border-amber-300 px-2 py-1 text-[10px] font-semibold text-amber-800">
          {provider.fallbackReason}
        </div>
      )}

      {/* Legend. A status board is only readable if the reader knows what the
          colours mean; without this the beacons are just dots. Kept compact
          and bottom-left so it never covers the theatre. */}
      {ready && !loadError && (
        <div className="absolute bottom-16 left-2 z-10 rounded-lg border border-slate-300 bg-white/95 px-2 py-1.5 shadow text-[9px] leading-tight">
          <div className="font-black uppercase tracking-wider text-slate-400 mb-1">
            {lang === "mr" ? "चिन्हे" : "Legend"}
          </div>
          <Key color="#10B981" label={lang === "mr" ? "सामान्य" : "Normal"} />
          <Key color="#F59E0B" label={lang === "mr" ? "वाढीव" : "Elevated"} />
          <Key color="#DC2626" label={lang === "mr" ? "गंभीर" : "Critical"} />
          <div className="mt-1 pt-1 border-t border-slate-200 text-slate-500">
            <div>
              <span className="inline-block h-2 w-2 rounded-full bg-slate-700 align-middle mr-1" />
              {lang === "mr" ? "अंकातील जोखीम गुण" : "number = risk score"}
            </div>
            <div>
              <span className="inline-block h-2 w-2 rounded-full ring-2 ring-white bg-orange-600 align-middle mr-1" />
              {lang === "mr" ? "शिफारस बिंदू" : "recommendation"}
            </div>
            <div>
              <span className="inline-block h-2 w-2 rounded-full border border-orange-600 align-middle mr-1" />
              {lang === "mr" ? "अडथळा ×" : "bottleneck ×"}
            </div>
          </div>
        </div>
      )}

      {/* Dev-only diagnostic strip. Remove nothing to ship it — the guard
          above means it simply does not render in a production build. */}
      {diag && (
        <div className="absolute top-2 left-2 z-20 rounded-md border border-slate-400 bg-white/95 px-2 py-1 text-[9px] font-mono leading-tight text-slate-700 shadow max-w-[240px]">
          <div className="font-bold text-slate-500">map diagnostics (dev)</div>
          <div>style {String(diag.styleLoaded)} · z{diag.zoom} · {diag.layers} layers</div>
          <div>base {diag.base} · sat {diag.sat}</div>
          <div>sectors {diag.sectors}</div>
          <div>risk {diag.risk}</div>
          <div>heat {diag.heat} · inc {diag.inc}</div>
        </div>
      )}

      <button
        type="button"
        onClick={resetView}
        className="absolute bottom-16 right-2 z-10 rounded-md bg-white/95 border border-slate-300 px-2 py-1 text-[11px] font-bold text-slate-700 shadow hover:bg-white"
      >
        Reset view
      </button>
    </div>
  );
}

function Key({ color, label }) {
  return (
    <div className="flex items-center gap-1 text-slate-600">
      <span className="h-2.5 w-2.5 rounded-full ring-1 ring-white" style={{ background: color }} />
      {label}
    </div>
  );
}

/* ── Small helpers ─────────────────────────────────────────────────────── */
function addSource(map, id, data, extra = {}) {
  if (map.getSource(id)) { map.getSource(id).setData(data); return; }
  map.addSource(id, { type: "geojson", data, ...extra });
}
function addLayer(map, spec) {
  if (map.getLayer(spec.id)) return;
  map.addLayer({ ...spec, layout: { visibility: "none", ...(spec.layout || {}) } });
}
function setVisible(map, id, on) {
  if (!map.getLayer(id)) return;
  map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
}
